import { canPasteNode } from '../../../components/Tree/helpers/canPasteHandlers';
import type { NodeBase } from '../../../entities/Node';

export type CatalogConfigProps = {
  catalogId: string;
  productsCatalogId: string;
  productsDraftCatalogId?: string;
  isEditorForCurrentMarket: boolean;
  isGlobalEditor: boolean;
  isGlobalMarket: boolean;
  isBoxEditor: boolean;
  isStoreEditor: boolean;
};

export const IMAGE_EDITABLE = 'EDIT' as const;
export const IMAGE_VIEW = 'VIEW' as const;
export const IMAGE_DISABLED = 'DISABLED' as const;

export default class DefaultCatalogConfig {
  allowedSources;

  allowHoverActions = false;

  allowCopyCategory = false;

  allowCopyToCatalog: string[] = [];

  allowCutCategory = false;

  allowRanking = false;

  allowRankingLevels: number[] = [];

  editable = false;

  indentTree = false;

  moveDisabled = true;

  newCategory = false;

  onlyExpandSearchedNodes = true;

  isBoxEditor = false;

  isStoreEditor = false;

  isEditorForCurrentMarket = false;

  catalog = {
    translation: false,
    availableForMarkets: false,
    createDraft: false,
    activateDraft: false,
  };

  item = {
    copy: false,
    remove: false,
    cut: false,
    approve: false,
    reject: false,
  };

  // category details actions
  category: {
    levelRestriction: boolean;
    levelEditable?: number[];
    hide: boolean;
    rename: boolean;
    translation: boolean;
    comment: boolean;
    delete: boolean;
    deleteCategoryLevels?: number[];
    publish: boolean;
    autoCategorisable: boolean;
    import: boolean;
    itemLimit?: number;
    categoryType: boolean;
    image: typeof IMAGE_EDITABLE | typeof IMAGE_VIEW | typeof IMAGE_DISABLED;
    categoryImageLevelRestriction?: number[];
    redirectIdRequired: boolean;
    productImage: typeof IMAGE_EDITABLE | typeof IMAGE_VIEW | typeof IMAGE_DISABLED;
    publishMinDaysDelay: number;
  } = {
    levelRestriction: false,
    deleteCategoryLevels: [],
    hide: false,
    rename: false,
    translation: false,
    comment: false,
    delete: false,
    publish: false,
    autoCategorisable: false,
    import: false,
    categoryType: false,
    image: IMAGE_DISABLED,
    categoryImageLevelRestriction: [],
    productImage: IMAGE_DISABLED,
    redirectIdRequired: true,
    publishMinDaysDelay: 1,
  };

  constructor({
    catalogId,
    productsCatalogId,
    isEditorForCurrentMarket,
    isGlobalMarket,
  }: CatalogConfigProps) {
    this.editable = isGlobalMarket && isEditorForCurrentMarket;

    this.category = {
      ...this.category,
      autoCategorisable: this.editable,
      categoryType: false,
      comment: isEditorForCurrentMarket,
      delete: isGlobalMarket && isEditorForCurrentMarket,
      import: this.editable,
      image: isEditorForCurrentMarket ? IMAGE_EDITABLE : IMAGE_VIEW,
      productImage: isEditorForCurrentMarket ? IMAGE_EDITABLE : IMAGE_VIEW,
      hide: isEditorForCurrentMarket,
      levelRestriction: false,
      publish: this.editable,
      rename: this.editable,
      translation: isEditorForCurrentMarket,
    };

    this.item = {
      copy: this.editable,
      remove: this.editable,
      cut: this.editable,
      approve: this.editable,
      reject: this.editable,
    };

    this.indentTree = this.editable;
    this.catalog.translation = !isGlobalMarket && isEditorForCurrentMarket;

    this.allowedSources = [catalogId];
    if (catalogId !== productsCatalogId) {
      this.allowedSources.push(productsCatalogId);
    }

    this.canPaste = this.canPaste.bind(this);
    this.canCopyToCatalogType = this.canCopyToCatalogType.bind(this);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  canCopyNode(node: NodeBase) {
    return true;
  }

  canCopyToCatalogType(catalogType: string | undefined): boolean {
    if (!this.allowCopyCategory) return false;
    if (!catalogType) return false;

    if (this.editable) return true;

    return this.allowCopyToCatalog.includes(catalogType);
  }

  canPaste(eventArgs: { nextParent: NodeBase; node: NodeBase }) {
    if (
      !(this.editable || this.allowRanking) ||
      !this.allowedSources.includes(eventArgs?.node?.catalogId)
    ) {
      return false;
    }
    return canPasteNode(eventArgs, this.moveDisabled);
  }
}
